import { forwardRef, memo, useMemo } from 'react';
import { StyleProp, View, ViewProps, ViewStyle } from 'react-native';

export type StyleProps = {
  flexDirection?: ViewStyle['flexDirection'];
  alignItems?: ViewStyle['alignItems'];
  justifyContent?: ViewStyle['justifyContent'];
  flexWrap?: ViewStyle['flexWrap'];
  flex?: ViewStyle['flex'];
  flexGrow?: ViewStyle['flexGrow'];
  flexShrink?: ViewStyle['flexShrink'];
  alignSelf?: ViewStyle['alignSelf'];
  flexBasis?: ViewStyle['flexBasis'];

  opacity?: number;
  backgroundColor?: ViewStyle['backgroundColor'];

  width?: ViewStyle['width'];
  height?: ViewStyle['height'];
  minWidth?: ViewStyle['minWidth'];
  minHeight?: ViewStyle['minHeight'];
  maxWidth?: ViewStyle['maxWidth'];
  maxHeight?: ViewStyle['maxHeight'];

  position?: ViewStyle['position'];
  left?: ViewStyle['left'];
  top?: ViewStyle['top'];
  right?: ViewStyle['right'];
  bottom?: ViewStyle['bottom'];
  zIndex?: ViewStyle['zIndex'];
  overflow?: ViewStyle['overflow'];

  borderWidth?: ViewStyle['borderWidth'];
  borderColor?: ViewStyle['borderColor'];
  borderStyle?: ViewStyle['borderStyle'];

  borderBottomColor?: ViewStyle['borderColor'];
  borderLeftColor?: ViewStyle['borderColor'];
  borderRightColor?: ViewStyle['borderColor'];
  borderTopColor?: ViewStyle['borderColor'];

  borderBottomWidth?: ViewStyle['borderWidth'];
  borderLeftWidth?: ViewStyle['borderWidth'];
  borderRightWidth?: ViewStyle['borderWidth'];
  borderTopWidth?: ViewStyle['borderWidth'];

  borderRadius?: ViewStyle['borderRadius'];
  borderTopLeftRadius?: ViewStyle['borderTopLeftRadius'];
  borderTopRightRadius?: ViewStyle['borderTopRightRadius'];
  borderBottomLeftRadius?: ViewStyle['borderBottomLeftRadius'];
  borderBottomRightRadius?: ViewStyle['borderBottomRightRadius'];

  margin?: number | 'auto';
  ml?: number | 'auto';
  mt?: number | 'auto';
  mr?: number | 'auto';
  mb?: number | 'auto';
  marginHorizontal?: number | 'auto';
  marginVertical?: number | 'auto';

  padding?: number | 'auto';
  pl?: number | 'auto';
  pt?: number | 'auto';
  pr?: number | 'auto';
  pb?: number | 'auto';
  paddingHorizontal?: number | 'auto';
  paddingVertical?: number | 'auto';
};

export const useViewStyleProps = ({
  width,
  height,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,

  flexDirection,
  alignItems,
  justifyContent,
  flexWrap,
  flex,
  alignSelf,
  flexGrow,
  flexShrink,
  flexBasis,

  opacity,
  backgroundColor,

  position,
  left,
  top,
  right,
  bottom,
  zIndex,
  overflow,

  borderWidth,
  borderColor,
  borderStyle,

  borderBottomWidth,
  borderLeftWidth,
  borderRightWidth,
  borderTopWidth,

  borderBottomColor,
  borderLeftColor,
  borderRightColor,
  borderTopColor,

  borderRadius,
  borderTopLeftRadius,
  borderTopRightRadius,
  borderBottomLeftRadius,
  borderBottomRightRadius,

  margin,
  ml,
  mr,
  mt,
  mb,
  marginHorizontal,
  marginVertical,

  padding,
  pl,
  pr,
  pt,
  pb,
  paddingHorizontal,
  paddingVertical,

  style,
  ...props
}: StyleProps & ViewProps) => {
  const viewStyle = useMemo<ViewStyle>(
    () => ({
      width,
      height,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight,

      flexDirection,
      justifyContent,
      alignItems,
      flexWrap,
      flex,
      alignSelf,
      flexGrow,
      flexShrink,
      flexBasis,

      position,
      left,
      top,
      right,
      bottom,
      zIndex,
      overflow,

      borderColor,
      borderStyle,
      borderBottomWidth,
      borderLeftWidth,
      borderRightWidth,
      borderTopWidth,
      borderWidth,

      borderBottomColor,
      borderLeftColor,
      borderRightColor,
      borderTopColor,

      borderRadius,
      borderTopLeftRadius,
      borderTopRightRadius,
      borderBottomLeftRadius,
      borderBottomRightRadius,

      marginLeft: ml ?? marginHorizontal ?? margin,
      marginRight: mr ?? marginHorizontal ?? margin,
      marginTop: mt ?? marginVertical ?? margin,
      marginBottom: mb ?? marginVertical ?? margin,

      paddingLeft: pl ?? paddingHorizontal ?? padding,
      paddingRight: pr ?? paddingHorizontal ?? padding,
      paddingTop: pt ?? paddingVertical ?? padding,
      paddingBottom: pb ?? paddingVertical ?? padding,

      backgroundColor,
      opacity,
    }),
    [
      alignItems,
      alignSelf,
      backgroundColor,
      borderBottomColor,
      borderBottomLeftRadius,
      borderBottomRightRadius,
      borderBottomWidth,
      borderColor,
      borderLeftColor,
      borderLeftWidth,
      borderRadius,
      borderRightColor,
      borderRightWidth,
      borderStyle,
      borderTopColor,
      borderTopLeftRadius,
      borderTopRightRadius,
      borderTopWidth,
      borderWidth,
      bottom,
      flex,
      flexBasis,
      flexDirection,
      flexGrow,
      flexShrink,
      flexWrap,
      height,
      justifyContent,
      left,
      margin,
      marginHorizontal,
      marginVertical,
      maxHeight,
      maxWidth,
      mb,
      minHeight,
      minWidth,
      ml,
      mr,
      mt,
      opacity,
      overflow,
      padding,
      paddingHorizontal,
      paddingVertical,
      pb,
      pl,
      position,
      pr,
      pt,
      right,
      top,
      width,
      zIndex,
    ],
  );

  return [
    [viewStyle, style] as StyleProp<ViewStyle>,
    props as ViewProps,
  ] as const;
};

export type EulerViewProps = ViewProps & StyleProps;

const EulerView = memo(
  forwardRef<View, EulerViewProps>((props, ref) => {
    const [style, viewProps] = useViewStyleProps(props);
    return <View ref={ref} style={style} {...viewProps} />;
  }),
);

export default EulerView;
